import { TriangleAlert } from 'lucide-react'
import { Card, Icon } from '@juice/design-system'
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { Button } from '@/components/ui/button/button'

/**
 * Global route-level error fallback (`router.defaultErrorComponent`).
 *
 * Any error thrown by a route loader or component that isn't handled locally
 * bubbles to TanStack Router's per-route error boundary. Without this, that
 * boundary renders the library's unstyled default ("Something went wrong! /
 * Show Error" with a raw stack) — the screen this replaces. Covers the residual
 * cases the `$projectId` guard deliberately re-throws (5xx, network) plus any
 * unexpected throw anywhere else in the app.
 */
export function RouteErrorScreen({ error }: ErrorComponentProps) {
  const router = useRouter()

  return (
    <div
      data-visual-route-error
      className="flex min-h-screen items-center justify-center bg-secondary px-6"
    >
      <Card
        padding={{ block: 40, inline: 32 }}
        elevation="raised"
        className="w-full max-w-md items-center gap-6 text-center"
      >
        <span className="flex size-14 items-center justify-center text-grey-600">
          <Icon icon={TriangleAlert} size={24} aria-hidden />
        </span>

        <div className="flex flex-col gap-2">
          <h1 className="font-inter text-lg font-semibold text-foreground">
            Something went wrong
          </h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            We hit an unexpected error loading this page. This is usually
            temporary &mdash; try again.
          </p>
        </div>

        <div className="flex w-full flex-col items-center gap-1.5">
          <Button
            width="fill"
            variant="default"
            onClick={() => {
              // Re-run loaders and reset the error boundary for this subtree.
              void router.invalidate()
            }}
          >
            Try Again
          </Button>
          <Button
            variant="ghost"
            data-navigation-intent="page"
            onClick={() => router.navigate({ to: '/dashboard', replace: true })}
          >
            Back to Dashboard
          </Button>
        </div>

        {import.meta.env.DEV && (
          <pre className="mt-1 max-h-40 w-full overflow-auto rounded-lg bg-grey-100 p-3 text-left text-xs text-grey-600">
            {error.message}
          </pre>
        )}
      </Card>
    </div>
  )
}
